var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var IncentivesExportConfig_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { JSON, JsonDiscriminatorValue, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScope } from './toolScope';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationResult, ToolValidationState } from './validation/toolValidationResult';
import { incentivesExportConfigSchemaValidation } from '../validations.generated';
export const incentivesExportConfigSchema = {
    $id: 'urn:wisepipe-common-pipe:IncentivesExportConfig',
    type: 'object',
    properties: {
        exportColumns: {
            type: 'array',
            items: { $ref: 'urn:wisepipe-common-data:Column' },
            uniqueItemProperties: ['name']
        },
        uniqueKeys: { type: 'array', items: { type: 'string' }, uniqueItems: true }
    },
    required: ['exportColumns']
};
let IncentivesExportConfig = IncentivesExportConfig_1 = class IncentivesExportConfig extends ToolConfig {
    constructor(exportColumns, uniqueKeys, customFormats) {
        super(ToolType.INCENTIVES_EXPORT, customFormats);
        this.exportColumns = exportColumns;
        this.uniqueKeys = uniqueKeys;
        JSON.validate(this, IncentivesExportConfig_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return IncentivesExportConfig_1.in;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return IncentivesExportConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return IncentivesExportConfig_1.outs;
    }
    static get in() {
        return IncentivesExportConfig_1.ins[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return [];
    }
    // we don't allow out edges, so we don't want an out() method
    // use this instead for storage location
    static get incentivesDataOut() {
        return 'default';
    }
    static get info() {
        return new ToolConfigInfo('Calculation Output', [ToolCategories.DATA], this.ins, this.outs, ['save', 'output', 'export', 'incentives'], true);
    }
    getOutput(iConfigHelper, ins) {
        return ToolScope.empty;
    }
    getOutputForIncentivesImport(iConfigHelper, ins) {
        if (this.exportColumns.length === 0 || this.isValidForCalc(iConfigHelper, ins) !== ToolValidationState.VALID)
            return ToolScope.empty;
        const tsb = new ToolScopeBuilder(ins);
        const sourceMap = ins.schemas
            .at(this.in)
            .reduce((sourceMap, colMetadata) => sourceMap.set(colMetadata.name, colMetadata), new Map());
        const exp = tsb.export(IncentivesExportConfig_1.incentivesDataOut);
        const clearColumnFormats = new Map();
        // get metadata of existing columns
        for (const exportCol of this.exportColumns) {
            const col = sourceMap.get(exportCol.name);
            if (col) {
                if (ToolConfig.columnTypesAreEqual(exportCol.type, col.column.type)) {
                    exp.copy(this.in, col, false, true);
                }
                else {
                    throw Error(`Type does not match in exported column ${col.name}.`);
                }
                if (col.customFormat) {
                    clearColumnFormats.set(col.name, undefined);
                }
            }
            else {
                throw Error('Attempting to export a non-existent column.');
            }
        }
        exp.setColumnFormats(clearColumnFormats).initializeColumnFormats().setColumnFormats(this.customFormats);
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, scope) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, scope);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        if (!scope.schemas.at(this.in)) {
            return ToolValidationResult.getMissingSourceColumnsResult();
        }
        const insMap = scope.schemas
            .at(this.in)
            .reduce((sourceMap, colMetadata) => sourceMap.set(colMetadata.name, colMetadata), new Map());
        // all exported columns must exist in incoming data
        const missingExportColumns = this.exportColumns.filter((exportCol) => !insMap.has(exportCol.name));
        if (missingExportColumns.length > 0) {
            return ToolValidationResult.getInvalidResult(`The following columns do not exist in the input node: ${missingExportColumns.join(', ')}.`, `Please review the export columns and ensure they exist in the input node.`);
        }
        // all exported columns have the correct type specified
        const invalidTypeExportColumns = this.exportColumns.reduce((acc, exportCol) => {
            const expectedType = insMap.get(exportCol.name)?.column.type;
            if (expectedType && !ToolConfig.columnTypesAreEqual(expectedType, exportCol.type)) {
                acc.push({ col: exportCol, expectedType });
            }
            return acc;
        }, []);
        if (invalidTypeExportColumns.length > 0) {
            return ToolValidationResult.getInvalidResult(`The following columns have an incorrect type: ${invalidTypeExportColumns
                .map((col) => JSON.stringify(col))
                .join(', ')}.`, `Please review the export columns and ensure they have the correct type.`);
        }
        // all unique keys must match incoming scope and exist in export columns
        const uniqueKeys = scope.uniqueKeys.at(this.in);
        if ((this.uniqueKeys ?? []).length !== (uniqueKeys ?? []).length) {
            return ToolValidationResult.getInvalidResult('The unique keys do not match the incoming scope.', 'Please review the unique keys and ensure they match the incoming scope.');
        }
        if (this.uniqueKeys?.some((key, index) => key !== uniqueKeys[index] || !this.exportColumns.some((column) => column.name === key))) {
            return ToolValidationResult.getInvalidResult('Some unique keys are invalid.');
        }
        return ToolValidationResult.getValidResult();
    }
    incomingColsMatchConfig(scope) {
        // check if incoming cols in metadata match the export node config exactly
        const incomingCols = scope.schemas.at(this.in)?.map((colMeta) => colMeta.column);
        if (!incomingCols)
            return false;
        return (incomingCols.length === this.exportColumns.length &&
            incomingCols.every((incomingColsColumn) => this.exportColumns.some((exportColumnsColumn) => incomingColsColumn.name === exportColumnsColumn.name && incomingColsColumn.type === exportColumnsColumn.type)));
    }
};
IncentivesExportConfig = IncentivesExportConfig_1 = __decorate([
    JsonSchemaV2(incentivesExportConfigSchema, incentivesExportConfigSchemaValidation),
    JsonDiscriminatorValue(ToolType.INCENTIVES_EXPORT)
], IncentivesExportConfig);
export { IncentivesExportConfig };
//# sourceMappingURL=incentivesExportConfig.js.map